import { useCallback, useRef } from 'react';
import { unprojectToPlane, SCENE_VIEW } from '../engine/battlefield/projection';
import { turnToken } from '../engine/battlefield/tokens';
import { bearingBetween } from '../engine/battlefield/facing';
import { pointerToScene } from '../utils/scenePointer';

/**
 * The heading that points the hull AT the pointer, on the gesture's frozen
 * plane; null off the ground. Alt snaps to fifteen degrees - the same
 * "precision when you want it" idiom as the grid snap.
 */
function headingTowardPointer(e, token, planeY, camera) {
  const { x: sceneX, y: sceneY } = pointerToScene(e);
  const ground = unprojectToPlane(sceneX, sceneY, planeY, camera, SCENE_VIEW);
  if (!ground) return null;
  const deg = bearingBetween(token, ground);
  return e.altKey ? Math.round(deg / 15) * 15 : deg;
}

/**
 * useHeadingDrag - turn a vehicle by dragging its heading handle.
 *
 * A SMALL SIBLING OF `useTokenDrag`, deliberately not a branch inside
 * it. Moving a marker needs CLICK, THEN CLICK AND HOLD, because a plain
 * drag on a marker body falls through to the tape and the body is
 * overloaded with four meanings. The heading handle is a dedicated
 * target with exactly one meaning, so it may take the press directly -
 * `stopPropagation` is what keeps it out of the measure gesture, and
 * pointer-events on the handle is what keeps it off the camera orbit.
 *
 * WHOEVER MAY MOVE A MARKER MAY TURN ONE. Turning decides which of a
 * vehicle's three armour values a shot meets - but so does dragging the
 * tank ten metres, and anybody may do that. Gating the rotation more
 * tightly than the move protects nothing and merely leaves a handle that
 * looks live and is not. The GM still overrules any of it from the
 * facing radio, which is a RULING and does need the grant (v8.57.0).
 */
export default function useHeadingDrag({ getCamera, tokens, writeToken, enabled = true }) {
  const drag = useRef(null);

  const onHeadingPointerDown = useCallback(
    (tokenId, e) => {
      if (!enabled || e.button !== 0) return;
      const token = tokens?.[tokenId];
      if (!token) return;
      // The handle owns this press outright: no measuring, no camera, no
      // click-then-hold arming to get wrong.
      e.stopPropagation();
      e.preventDefault();
      e.currentTarget.setPointerCapture?.(e.pointerId);
      // ONE PLANE FOR THE WHOLE GESTURE, frozen at the storey it began
      // on - the v8.7.0 rule. A hull that changes elevation mid-turn
      // would otherwise slide the mapping under the pointer.
      drag.current = { id: e.pointerId, tokenId, planeY: token.elevation ?? 0 };
    },
    [enabled, tokens],
  );
  const { onPointerMove, end } = useTurning(drag, { getCamera, tokens, writeToken });
  return {
    onHeadingPointerDown,
    /** True while a hull is being turned - the panel suppresses its own gestures. */
    isTurning: () => drag.current !== null,
    headingDragHandlers: { onPointerMove, onPointerUp: end, onPointerCancel: end },
  };
}

/** The turn this event belongs to, with its live token (null if gone), or null. */
function turnOf(drag, tokens, e) {
  const d = drag.current;
  if (!d || d.id !== e.pointerId) return null;
  return { d, token: tokens?.[d.tokenId] ?? null };
}

/** The turn in progress: follow the pointer, and flush the last heading on release. */
function useTurning(drag, { getCamera, tokens, writeToken }) {
  const onPointerMove = useCallback(
    (e) => {
      const t = turnOf(drag, tokens, e);
      if (!t?.token) return;
      const deg = headingTowardPointer(e, t.token, t.d.planeY, getCamera());
      if (deg !== null) writeToken(turnToken(t.token, deg));
    },
    [drag, tokens, getCamera, writeToken],
  );
  const end = useCallback(
    (e) => {
      const t = turnOf(drag, tokens, e);
      if (!t) return;
      e.currentTarget.releasePointerCapture?.(e.pointerId);
      drag.current = null;
      // Flush the debounce, as a move does on release: a heading left in
      // the queue is a heading the other clients have not seen.
      if (t.token) writeToken(t.token, { immediate: true });
    },
    [drag, tokens, writeToken],
  );
  return { onPointerMove, end };
}
